import { useState, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { loadFromCookie, saveJsonToCookie } from '../utils/cookieUtils';

const DATE_TAGS_COOKIE_KEY = 'daylist_date_tags';
const MAX_DATE_TAGS = 10;

const loadDateTagsFromCookie = (): string[] => loadFromCookie(DATE_TAGS_COOKIE_KEY, []);
const saveDateTagsToCookie = (tags: string[]): void => saveJsonToCookie(DATE_TAGS_COOKIE_KEY, tags, 30);

interface ToastState {
  open: boolean;
  message: string;
  severity: string;
}

interface DisplayDateTag {
  date: string;
  label: string;
  isDefault: boolean;
}

interface UseDateTagsOptions {
  onToast: (toastState: ToastState) => void;
  onDateSelect: (date: string) => void;
}

/**
 * useDateTags
 *
 * 日付タグの状態・表示データ・操作ハンドラを管理するフック。
 * DayListReservations から抽出。
 */
export function useDateTags({ onToast, onDateSelect }: UseDateTagsOptions) {
  const [, setSearchParams] = useSearchParams();
  const [dateTags, setDateTags] = useState<string[]>(() => loadDateTagsFromCookie());
  const [addDatePickerValue, setAddDatePickerValue] = useState<string>('');

  const displayDateTags = useMemo((): DisplayDateTag[] => {
    const today = dayjs().format('YYYY-MM-DD');
    const tomorrow = dayjs().add(1, 'day').format('YYYY-MM-DD');

    const defaultTags: DisplayDateTag[] = [
      { date: today, label: '本日', isDefault: true },
      { date: tomorrow, label: '明日', isDefault: true },
    ];

    const userTags: DisplayDateTag[] = dateTags
      .filter(date => date !== today && date !== tomorrow)
      .map(date => ({ date, label: dayjs(date).format('MM/DD'), isDefault: false }));

    return [...defaultTags, ...userTags];
  }, [dateTags]);

  const handleAddDateTag = useCallback(() => {
    if (!addDatePickerValue) return;

    const today = dayjs().format('YYYY-MM-DD');
    const tomorrow = dayjs().add(1, 'day').format('YYYY-MM-DD');

    if (addDatePickerValue === today || addDatePickerValue === tomorrow) {
      setAddDatePickerValue('');
      return;
    }

    if (dateTags.includes(addDatePickerValue)) {
      setAddDatePickerValue('');
      return;
    }

    if (dateTags.length >= MAX_DATE_TAGS - 2) {
      onToast({ open: true, message: '日付タグは最大10個までです', severity: 'warning' });
      return;
    }

    const newTags = [...dateTags, addDatePickerValue].sort();
    setDateTags(newTags);
    saveDateTagsToCookie(newTags);
    setAddDatePickerValue('');
  }, [addDatePickerValue, dateTags, onToast]);

  const handleRemoveDateTag = useCallback((dateToRemove: string) => {
    const newTags = dateTags.filter(date => date !== dateToRemove);
    setDateTags(newTags);
    saveDateTagsToCookie(newTags);
  }, [dateTags]);

  const handleDateTagClick = useCallback((date: string) => {
    onDateSelect(date);
    // ?vehicle= 等の他クエリを消さないよう date だけ更新
    setSearchParams(prev => { const p = new URLSearchParams(prev); p.set('date', date); return p; });
  }, [onDateSelect, setSearchParams]);

  return {
    dateTags,
    displayDateTags,
    addDatePickerValue,
    setAddDatePickerValue,
    handleAddDateTag,
    handleRemoveDateTag,
    handleDateTagClick,
  };
}
